import { Navigate, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box } from '@mui/material'
import { formatCPF, removeNonNumeric } from '@/helpers'
import { routes } from '@/router'
import { sessionButtonSx } from '../session-button'
import {
  RedirectIfAuthenticated,
  SessionField,
  SessionScreen,
} from '../session-screen'
import { flowProgress } from '../profile/profile-steps'
import { useSignupDraft } from './use-signup-draft'
import { CpfFormData, cpfFormSchema } from './signup.schema'

function formatCpfInput(value: string) {
  const digits = removeNonNumeric(value).slice(0, 11)
  if (digits.length < 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
  }
  return formatCPF(digits)
}

export function SignupCpfPage() {
  const navigate = useNavigate()
  const { draft, setTaxId } = useSignupDraft()

  const { handleSubmit, control, watch } = useForm<CpfFormData>({
    resolver: zodResolver(cpfFormSchema),
    defaultValues: { taxId: draft.taxId },
    mode: 'onChange',
  })

  const taxId = watch('taxId')
  const isValid = cpfFormSchema.safeParse({ taxId }).success

  if (!draft.fullName.trim()) {
    return <Navigate to={routes.session.signup.name} replace />
  }

  return (
    <RedirectIfAuthenticated>
      <Box
        component="form"
        onSubmit={handleSubmit(({ taxId: nextTaxId }) => {
          setTaxId(nextTaxId)
          navigate(routes.session.signup.email)
        })}
        sx={{ display: 'flex', flex: 1 }}
      >
        <SessionScreen
          title="Digite seu CPF"
          subtitle="Usamos o CPF para reconhecer sua conta."
          progress={flowProgress('cpf', null)}
          onBack={() => navigate(routes.session.signup.name)}
          footer={
            <LoadingButton
              type="submit"
              size="large"
              fullWidth
              disabled={!isValid}
              sx={sessionButtonSx()}
            >
              Continuar
            </LoadingButton>
          }
        >
          <Controller
            name="taxId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <SessionField
                {...field}
                id="signup-cpf"
                label="CPF"
                autoComplete="off"
                placeholder="000.000.000-00"
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                errorText={
                  removeNonNumeric(field.value).length >= 11
                    ? error?.message
                    : undefined
                }
                onChange={(event) => {
                  field.onChange(formatCpfInput(event.target.value))
                }}
                autoFocus
              />
            )}
          />
        </SessionScreen>
      </Box>
    </RedirectIfAuthenticated>
  )
}
